import { Flex, LoadingOverlay, Stack } from '@mantine/core';
import { useParams } from 'react-router-dom';
import { Paper } from '../../../components/Paper';
import { SmallModal } from '../../../components/SmallModal';
import { useModal } from '../../../hooks/useModal';
import { mutate } from '../../../util/api/mutate';
import { useEditStaffMemberForm } from '../hooks/useEditStaffMemberForm';
import { useServerStaffValidation } from '../hooks/useServerStaffValidation';
import type { FullOperatorDto } from '../types/OperatorDto';
import {
	UpsertStaffAddress,
	UpsertStaffEmergencyContact,
	UpsertStaffGeneralInfo,
} from './UpsertStaffMemberComponents';

interface StaffMemberViewProps {
	originalOperator: FullOperatorDto;
	onEditComplete: () => void;
}

export function EditStaffMemberForm({
	originalOperator,
	onEditComplete,
}: StaffMemberViewProps) {
	const { organisation } = useParams();

	const successModal = useModal();

	const { form, inputs } = useEditStaffMemberForm(originalOperator);
	const editOperatorMutation = mutate<FullOperatorDto>(
		`${organisation}/operators/edit`,
		inputs,
	);

	useServerStaffValidation(editOperatorMutation, form);

	return (
		<>
			<SmallModal
				isOpen={successModal.isOpen}
				close={successModal.close}
				onComplete={onEditComplete}
				title={'Staff Member Edited!'}
				body={
					<>
						Staff member has been successfully edited. Click Ok to continue...
					</>
				}
			/>

			<form
				id={'edit-staff-form'}
				onSubmit={form.onSubmit(async () => {
					await editOperatorMutation.mutateAsync().then((response) => {
						if (response.isValid) {
							successModal.open();
						}
					});
				})}
			>
				<LoadingOverlay visible={editOperatorMutation.isLoading} />
				<Flex w={'100%'} h={'100%'} dir={'column'} gap={20}>
					<Stack w={'50%'}>
						<Paper title={'General Info'}>
							<UpsertStaffGeneralInfo form={form} />
						</Paper>
					</Stack>
					<Stack w={'50%'}>
						<Paper title={'Address'}>
							<UpsertStaffAddress form={form} />
						</Paper>

						<Paper title={'Emergency Contact'}>
							<UpsertStaffEmergencyContact form={form} />
						</Paper>
					</Stack>
				</Flex>
			</form>
		</>
	);
}
